import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { alpha } from "@mui/material/styles";
import { type ReactNode, memo, useCallback, useMemo, useState } from "react";
import { IoPlayCircleSharp } from "react-icons/io5";
import { MdOutlineSportsSoccer, MdReceiptLong } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";

import AppIcon, { IconType } from "@/components/Core/AppIcon";
import { routers } from "@/configs";
import { useSiteSearch, useTranslate } from "@/hooks";
import { MobileFooterContainer } from "@/layout/styles";
import { useDispatch, useSelector } from "@/store";
import { openBetSlip as openSlip } from "@/store/reducers/betSlip";
import { colors } from "@/theme";

import SideMenuMobile from "../Sidebar/Mobile";

/** Where the sportsbook's pages live; anything else is the casino's. */
const SPORT_PATH_PREFIX = "/sports";

type FooterItem = {
  key: string;
  icon: IconType;
  /** Drawn instead of the AppIcon when set (the sportsbook items use react-icons). */
  iconNode?: ReactNode;
  translationKey: string;
  /** Lit: the place the player is, or the panel that is open. */
  active: boolean;
  /** A count on the icon: the selections waiting in the bet slip. */
  badge?: number;
  onClick: () => void;
};

interface MobileFooterProps {
  isSport?: boolean;
}

/**
 * The bar across the bottom of a phone: the menu, the two halves of the site,
 * and the bet slip.
 *
 * It did not say where the player was - all four items looked the same on every
 * page - and each item was set a third of the width, so four of them overflowed
 * the screen. Now the four share the width, the one the player is on is lit in
 * the brand blue with a bar over it, the bet slip carries the number of
 * selections in it, and the bar stands clear of a phone's home indicator.
 */
export const MobileFooter = memo(({ isSport }: MobileFooterProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = useTranslate();

  const dispatch = useDispatch();
  // Only the open flag and the count: the whole slip would re-render the bar on every stake typed.
  const openBetSlip = useCallback(() => dispatch(openSlip()), [dispatch]);
  const betSlipOpen = useSelector((state) => state.betSlip.isOpen);
  const betCount = useSelector((state) => state.betSlip.bets.length);

  const toggleDrawer = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const { isOpen: searchOpen, open: openSearch } = useSiteSearch();
  const onSportsPage = pathname.startsWith(SPORT_PATH_PREFIX);

  const onCasinoClick = useCallback(
    () => navigate(routers.CasinoGame.replace(":code", "lobby")),
    [navigate],
  );
  const onSearchClick = useCallback(() => openSearch(), [openSearch]);
  const onSportsClick = useCallback(() => navigate(routers.SportsInPlay), [navigate]);
  const onLobbyClick = useCallback(() => navigate(routers.SportsLobby), [navigate]);
  const onMyBetsClick = useCallback(() => navigate(routers.MyBetsSport), [navigate]);
  const onLobby = pathname === routers.SportsLobby;
  const onLive = pathname === routers.SportsInPlay;
  const onMyBets = pathname.startsWith(routers.MyBetsSport);
  const onBetSlipClick = useCallback(() => {
    // The slip lives in the sportsbook. Already there, it just opens.
    if (onSportsPage) {
      openBetSlip();
      return;
    }
    navigate(routers.SportsInPlay);
    setTimeout(() => openBetSlip(), 200);
  }, [navigate, openBetSlip, onSportsPage]);

  const panelOpen = open || searchOpen || betSlipOpen;
  // In the sportsbook the bar is the sportsbook's: Lobby · Live · My bets · Slip · Menu (the casino is in the menu).
  const sportItems: FooterItem[] = [
    {
      key: "lobby",
      icon: "sports",
      iconNode: <MdOutlineSportsSoccer size={20} />,
      translationKey: "sport.lobby",
      active: onLobby && !panelOpen,
      onClick: onLobbyClick,
    },
    {
      key: "live",
      icon: "sports",
      iconNode: <IoPlayCircleSharp size={20} />,
      translationKey: "sport.live",
      active: onLive && !panelOpen,
      onClick: onSportsClick,
    },
    {
      key: "my-bets",
      icon: "document2",
      iconNode: <MdReceiptLong size={20} />,
      translationKey: "navigation.my-bets",
      active: onMyBets && !panelOpen,
      onClick: onMyBetsClick,
    },
    {
      key: "betslip",
      icon: "document2",
      translationKey: "bet-slip.title",
      active: betSlipOpen && !open,
      badge: betCount,
      onClick: onBetSlipClick,
    },
    {
      key: "menu",
      icon: "menu2",
      translationKey: "footer.menu",
      active: open,
      onClick: toggleDrawer,
    },
  ];

  const items = useMemo<FooterItem[]>(
    () => [
      {
        key: "menu",
        icon: "menu2",
        translationKey: "footer.menu",
        active: open,
        onClick: toggleDrawer,
      },
      {
        key: "casino",
        icon: "casino",
        translationKey: "footer.casino",
        // One thing is lit at a time: an open panel takes it from the page under it.
        active: !onSportsPage && !searchOpen && !open && !betSlipOpen,
        onClick: onCasinoClick,
      },
      {
        key: "search",
        icon: "search",
        translationKey: "search.title",
        active: searchOpen && !open && !betSlipOpen,
        onClick: onSearchClick,
      },
      {
        key: "sports",
        icon: "sports",
        translationKey: "sport.sports",
        active: onSportsPage && !searchOpen && !open && !betSlipOpen,
        onClick: onLobbyClick,
      },
      {
        key: "betslip",
        icon: "document2",
        translationKey: "bet-slip.title",
        active: betSlipOpen && !open,
        badge: betCount,
        onClick: onBetSlipClick,
      },
    ],
    [
      open,
      onSportsPage,
      searchOpen,
      betSlipOpen,
      betCount,
      toggleDrawer,
      onCasinoClick,
      onSearchClick,
      onLobbyClick,
      onBetSlipClick,
    ],
  );

  return (
    <>
      <MobileFooterContainer component="nav">
        {(isSport ? sportItems : items).map(
          ({ key, icon, iconNode, translationKey, active, badge, onClick }) => (
            <ButtonBase
              key={key}
              onClick={onClick}
              aria-label={t(translationKey)}
              aria-current={active ? "page" : undefined}
              sx={(theme) => ({
                position: "relative",
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                pt: 1,
                pb: 0.75,
                fontFamily: "inherit",
                fontSize: 11,
                fontWeight: active ? 700 : 500,
                lineHeight: 1.2,
                color: active ? colors.prime : theme.palette.text.secondary,
                transition: "color 160ms ease, transform 120ms ease",
                "&:active": { transform: "scale(0.94)" },
                "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: -2 },
                // The bar over the item the player is on.
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  width: 28,
                  height: 3,
                  borderRadius: "0 0 3px 3px",
                  backgroundColor: colors.prime,
                  transform: `translateX(-50%) scaleX(${active ? 1 : 0})`,
                  transition: "transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                  "&:active": { transform: "none" },
                  "&::before": { transition: "none" },
                },
              })}
            >
              <Box
                // A new count replays the bump: a phone's slip stays shut, so this shows the pick landed.
                key={badge ? `count-${badge}` : "icon"}
                sx={{
                  display: "grid",
                  placeItems: "center",
                  width: 44,
                  height: 28,
                  borderRadius: 999,
                  backgroundColor: active ? alpha(colors.prime, 0.16) : "transparent",
                  transition: "background-color 160ms ease",
                  ...(badge
                    ? {
                        animation: "footerBadgeBump 360ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                        "@keyframes footerBadgeBump": {
                          "0%": { transform: "scale(1)" },
                          "40%": { transform: "scale(1.18)" },
                          "100%": { transform: "scale(1)" },
                        },
                      }
                    : {}),
                  "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                    animation: "none",
                  },
                }}
              >
                <Badge
                  badgeContent={badge}
                  color="primary"
                  max={99}
                  sx={{ "& .MuiBadge-badge": { fontSize: 10, minWidth: 16, height: 16, px: 0.5 } }}
                >
                  {iconNode ?? <AppIcon size={20} name={icon} />}
                </Badge>
              </Box>
              <Box
                component="span"
                sx={{
                  maxWidth: "100%",
                  px: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t(translationKey)}
              </Box>
            </ButtonBase>
          ),
        )}
      </MobileFooterContainer>
      <SideMenuMobile open={open} toggleDrawer={toggleDrawer} isSport={isSport} />
    </>
  );
});
MobileFooter.displayName = "MobileFooter";
